import React, { useState, useEffect } from 'react';
import { Plus, Search, RefreshCw, Receipt, Eye, CheckCircle, Clock, X, AlertCircle, Pencil } from 'lucide-react';
import AssignPaymentModal      from './AssignPaymentModal';
import EditTransactionModal    from './EditTransactionModal';
import ExportTransactions      from './ExportTransactions';
import RecordPayment           from './RecordPayment';
import EditManualPaymentModal  from './EditManualPaymentModal';
import Api from './Api';
import { useAppTheme } from '../../contexts/ThemeContext';

const Payments = () => {
  const { palette, Colors } = useAppTheme();
  const [openDialog,             setOpenDialog]             = useState(false);
  const [searchTerm,             setSearchTerm]             = useState('');
  const [paymentPeriod,          setPaymentPeriod]          = useState('all');
  const [transactions,           setTransactions]           = useState([]);
  const [loading,                setLoading]                = useState(true);
  const [error,                  setError]                  = useState(null);
  const [assignPaymentOpen,      setAssignPaymentOpen]      = useState(false);
  const [editTransactionOpen,    setEditTransactionOpen]    = useState(false);
  const [editManualOpen,         setEditManualOpen]         = useState(false);
  const [selectedTransaction,    setSelectedTransaction]    = useState(null);
  const [snackbar,               setSnackbar]               = useState({ open: false, message: '', severity: 'success' });

  const fetchTransactions = async () => {
    setLoading(true);
    try { setTransactions(await Api.getTransactions()); setError(null); }
    catch (err) { setError(`Failed to fetch transactions: ${err.message}`); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTransactions(); }, []);

  const handleConfirmTransaction = async (data) => {
    try {
      const result = await Api.confirmTransaction(data.transaction_id, data.tenant_id);
      setTransactions(prev => prev.map(t => t.transaction_id === data.transaction_id ? { ...t, status: 'COMPLETED' } : t));
      setSnackbar({ open: true, message: result.tenant_name ? `Payment assigned to ${result.tenant_name}` : 'Transaction confirmed', severity: 'success' });
      setAssignPaymentOpen(false);
    } catch (err) { setSnackbar({ open: true, message: `Failed: ${err.message}`, severity: 'error' }); }
  };

  const handleDistributeTransaction = async (result) => {
    try {
      setTransactions(prev => prev.map(t => t.transaction_id === selectedTransaction.transaction_id ? { ...t, status: 'COMPLETED' } : t));
      setSnackbar({ open: true, message: `Payment distributed to ${result.tenant_names.join(', ')}`, severity: 'success' });
      fetchTransactions();
    } catch (err) { setSnackbar({ open: true, message: `Failed: ${err.message}`, severity: 'error' }); }
  };

  const handleGenerateReceipt = async (id) => {
    try { await Api.generateReceipt(id); setSnackbar({ open: true, message: 'Receipt generated', severity: 'success' }); }
    catch (err) { setSnackbar({ open: true, message: `Failed: ${err.message}`, severity: 'error' }); }
  };

  const handleEditManualSuccess = (result) => {
    // Update the row in-place with the new transaction_id and time
    setTransactions(prev => prev.map(t =>
      t.transaction_id === selectedTransaction.transaction_id
        ? { ...t, transaction_id: result.transaction_id, transaction_time: result.transaction_time }
        : t
    ));
    setSnackbar({ open: true, message: 'Payment updated successfully', severity: 'success' });
  };

  const filteredTransactions = transactions.filter(t => {
    const matchSearch = t.transaction_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.phone_number.includes(searchTerm) || t.company_ref.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchSearch) return false;
    if (paymentPeriod === 'pending')   return t.status === 'PENDING';
    if (paymentPeriod === 'completed') return t.status === 'COMPLETED';
    if (paymentPeriod === 'current')   return new Date(t.transaction_time).getMonth() === new Date().getMonth();
    return true;
  });

  // Inline style tokens
  const bg  = palette.background;
  const txt = palette.text;
  const bdr = palette.border;
  const P   = Colors.primary.main;

  const card  = { backgroundColor: bg.secondary, border: `1px solid ${bdr}`, borderRadius: '12px' };
  const input = { backgroundColor: bg.tertiary,  border: `1px solid ${bdr}`, color: txt.primary, outline: 'none' };

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg.primary }}>
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        <div className="rounded-xl p-6" style={card}>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: P }}>Online Payments</h1>
              <p className="mt-1" style={{ color: txt.secondary }}>Manage and monitor all payment transactions</p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={fetchTransactions} className="p-2.5 rounded-lg transition-colors" style={{ backgroundColor: bg.tertiary, color: txt.primary }} title="Refresh">
                <RefreshCw className="w-5 h-5" />
              </button>
              <button onClick={() => setOpenDialog(true)} className="text-white font-semibold px-6 py-2.5 rounded-lg flex items-center gap-2 transition-all" style={{ backgroundColor: P }}>
                <Plus className="w-5 h-5" /> Record Payment
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Export */}
      <div className="px-6 pb-4">
        <ExportTransactions transactions={transactions} />
      </div>

      {/* Filters */}
      <div className="px-6 pb-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="w-5 h-5" style={{ color: txt.muted }} />
            </div>
            <input type="text" placeholder="Search payments..." value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg transition-all" style={input} />
          </div>
          <div className="w-64">
            <select value={paymentPeriod} onChange={e => setPaymentPeriod(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg" style={input}>
              <option value="all">All Payments</option>
              <option value="current">Current Month</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && transactions.length === 0 ? (
        <div className="px-6 pb-6">
          <div className="rounded-xl p-12 text-center" style={card}>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: P }}></div>
            <p className="mt-4" style={{ color: txt.secondary }}>Loading transactions...</p>
          </div>
        </div>
      ) : error && transactions.length === 0 ? (
        <div className="px-6 pb-6">
          <div className="rounded-xl p-4 flex items-start gap-3" style={{ backgroundColor: `${Colors.status.error}10`, border: `1px solid ${Colors.status.error}40` }}>
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: Colors.status.error }} />
            <div>
              <p className="font-medium" style={{ color: Colors.status.error }}>{error}</p>
              <button onClick={fetchTransactions} className="mt-2 text-sm font-medium underline" style={{ color: Colors.status.error }}>Try Again</button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="px-6 pb-4">
            <p className="text-sm font-medium" style={{ color: txt.secondary }}>
              Showing <span className="font-bold" style={{ color: P }}>{filteredTransactions.length}</span> of <span className="font-bold">{transactions.length}</span> transactions
            </p>
          </div>

          <div className="px-6 pb-6">
            <div className="rounded-xl overflow-hidden" style={card}>
              <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: 500 }}>
                <table className="w-full">
                  <thead className="sticky top-0" style={{ backgroundColor: bg.tertiary, borderBottom: `1px solid ${bdr}` }}>
                    <tr>
                      {['Mpesa Ref','Time','Company Ref','Name','Phone','Amount','Status','Actions'].map(col => (
                        <th key={col} className={`px-6 py-4 text-xs font-bold uppercase ${col === 'Actions' ? 'text-center' : 'text-left'}`} style={{ color: txt.secondary }}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.length > 0 ? filteredTransactions.map(txn => (
                      <tr key={txn.transaction_id} style={{ borderBottom: `1px solid ${bdr}` }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = bg.tertiary}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <td className="px-6 py-4 text-sm" style={{ color: txt.primary }}>{txn.transaction_id}</td>
                        <td className="px-6 py-4 text-sm" style={{ color: txt.primary }}>{txn.transaction_time}</td>
                        <td className="px-6 py-4 text-sm" style={{ color: txt.primary }}>{txn.company_ref}</td>
                        <td className="px-6 py-4 text-sm font-medium" style={{ color: txt.primary }}>{txn.customer_name}</td>
                        <td className="px-6 py-4 text-sm" style={{ color: txt.primary }}>{txn.phone_number}</td>
                        <td className="px-6 py-4 text-sm font-semibold" style={{ color: txt.primary }}>KES {txn.amount.toFixed(2)}</td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full"
                            style={{
                              backgroundColor: txn.status === 'COMPLETED' ? `${Colors.status.success}15` : `${Colors.status.warning}15`,
                              color: txn.status === 'COMPLETED' ? Colors.status.success : Colors.status.warning,
                            }}>
                            {txn.status === 'COMPLETED' ? <CheckCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                            {txn.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {txn.status === 'PENDING' ? (
                            <div className="flex items-center justify-center gap-2">
                              <button onClick={() => { setSelectedTransaction(txn); setAssignPaymentOpen(true); }}
                                className="px-3 py-1.5 text-white text-sm font-medium rounded-lg transition-colors" style={{ backgroundColor: P }}>
                                Confirm
                              </button>
                              <button onClick={() => { setSelectedTransaction(txn); setEditTransactionOpen(true); }}
                                className="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors" style={{ backgroundColor: bg.tertiary, border: `1px solid ${bdr}`, color: txt.primary }}>
                                Edit
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              <button className="p-2 rounded-lg transition-colors" style={{ color: txt.secondary }} title="View Details">
                                <Eye className="w-5 h-5" />
                              </button>
                              <button onClick={() => handleGenerateReceipt(txn.transaction_id)} className="p-2 rounded-lg transition-colors" style={{ color: P }} title="Generate Receipt">
                                <Receipt className="w-5 h-5" />
                              </button>
                              {/* Edit icon — only for manually recorded completed payments */}
                              {txn.is_manual && (
                                <button
                                  onClick={() => { setSelectedTransaction(txn); setEditManualOpen(true); }}
                                  className="p-2 rounded-lg transition-colors"
                                  style={{ color: txt.secondary }}
                                  title="Edit manual payment">
                                  <Pencil className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center gap-2">
                            <AlertCircle className="w-12 h-12" style={{ color: txt.muted }} />
                            <p className="font-medium" style={{ color: txt.secondary }}>
                              {searchTerm ? 'No matching transactions found' : 'No transactions available'}
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Record Payment Modal */}
      <RecordPayment open={openDialog} onClose={() => setOpenDialog(false)} onSuccess={fetchTransactions} />

      {/* Edit Manual Payment Modal */}
      <EditManualPaymentModal
        open={editManualOpen}
        onClose={() => { setEditManualOpen(false); setSelectedTransaction(null); }}
        transaction={selectedTransaction}
        onSuccess={handleEditManualSuccess}
      />

      {/* Snackbar */}
      {snackbar.open && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className="flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg"
            style={{
              backgroundColor: snackbar.severity === 'success' ? `${Colors.status.success}15` : `${Colors.status.error}15`,
              border: `1px solid ${snackbar.severity === 'success' ? Colors.status.success : Colors.status.error}40`,
            }}>
            {snackbar.severity === 'success'
              ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: Colors.status.success }} />
              : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: Colors.status.error }} />}
            <p className="text-sm font-medium" style={{ color: snackbar.severity === 'success' ? Colors.status.success : Colors.status.error }}>
              {snackbar.message}
            </p>
            <button onClick={() => setSnackbar({ ...snackbar, open: false })} style={{ color: snackbar.severity === 'success' ? Colors.status.success : Colors.status.error }}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <AssignPaymentModal  open={assignPaymentOpen}   onClose={() => { setAssignPaymentOpen(false); setSelectedTransaction(null); }} transactionData={selectedTransaction} onConfirm={handleConfirmTransaction} />
      <EditTransactionModal open={editTransactionOpen} onClose={() => { setEditTransactionOpen(false); setSelectedTransaction(null); }} transactionData={selectedTransaction} onConfirm={handleDistributeTransaction} />
    </div>
  );
};

export default Payments;